import type { HarborPropKind } from '@/meshkit/props/harbor';
import type { VehicleKind } from '@/meshkit/vehicles/cars';

export type LevelZoneKind =
  | 'roadLane'
  | 'parkingBay'
  | 'sidewalk'
  | 'quay'
  | 'buildingFootprint'
  | 'serviceYard'
  | 'alley'
  | 'objectiveArea'
  | 'water'
  | 'intersection';

export type PlacementRole =
  | 'decorative'
  | 'solid-cover'
  | 'street-furniture'
  | 'vehicle'
  | 'facade-attached'
  | 'water-object'
  | 'mission-critical';

export interface LevelZone {
  readonly id: string;
  readonly kind: LevelZoneKind;
  readonly x: number;
  readonly z: number;
  readonly halfX: number;
  readonly halfZ: number;
  readonly intent: string;
  readonly allowedProps?: readonly HarborPropKind[];
  readonly allowedVehicles?: readonly VehicleKind[];
  readonly clearance?: number;
}

export interface MapSemantics {
  readonly districtName: string;
  readonly primaryRoute: string;
  readonly landmark: string;
  readonly zones: readonly LevelZone[];
}

export function zone(
  id: string,
  kind: LevelZoneKind,
  x: number,
  z: number,
  halfX: number,
  halfZ: number,
  intent: string,
  options: {
    readonly allowedProps?: readonly HarborPropKind[];
    readonly allowedVehicles?: readonly VehicleKind[];
    readonly clearance?: number;
  } = {},
): LevelZone {
  return { id, kind, x, z, halfX, halfZ, intent, ...options };
}

export function containsPoint(zoneValue: LevelZone, x: number, z: number, margin = 0): boolean {
  return (
    Math.abs(x - zoneValue.x) <= Math.max(0, zoneValue.halfX - margin)
    && Math.abs(z - zoneValue.z) <= Math.max(0, zoneValue.halfZ - margin)
  );
}

export function zoneContainsAabb(
  zoneValue: LevelZone,
  box: { readonly x: number; readonly z: number; readonly halfX: number; readonly halfZ: number },
  margin = 0,
): boolean {
  return (
    Math.abs(box.x - zoneValue.x) + box.halfX <= Math.max(0, zoneValue.halfX - margin)
    && Math.abs(box.z - zoneValue.z) + box.halfZ <= Math.max(0, zoneValue.halfZ - margin)
  );
}

export function zonesTouch(a: LevelZone, b: LevelZone, margin = 0.05): boolean {
  return (
    Math.abs(a.x - b.x) <= a.halfX + b.halfX + margin
    && Math.abs(a.z - b.z) <= a.halfZ + b.halfZ + margin
  );
}
